import { cardState } from '@littlebird/action-cards-view';
import { useState } from 'react';
import { toast } from 'sonner';

import type { RunEnd } from '@/features/action-cards-view';

import { CardRunToast } from './components/CardRunToast';

import type { ActionCard } from '@littlebird/sdk';

const RUNNING_TEXT = 'Working on it…';
const DONE_TEXT = 'Done';
const FAILED_TEXT = "Couldn't send";
const REFUSED_TEXT = 'This card needs a change before it can run';
const STILL_RUNNING_TEXT = 'Still running. It moves to History once it is done';

const DONE_STATUSES: ReadonlySet<ActionCard['status']> = new Set([
  'completed',
  'completed_externally',
]);

// How long the done toast stays up before it goes
const DONE_TOAST_MS = 2500;

const toastIdOf = (cardId: string) => `action-card-run:${cardId}`;
// A custom toast keeps rendering its content when updated, so a run's end that
// is not done takes a toast of its own
const endToastIdOf = (cardId: string) => `action-card-run-end:${cardId}`;

const withoutId = (ids: ReadonlySet<string>, cardId: string) =>
  ids.has(cardId) ? new Set([...ids].filter((id) => id !== cardId)) : ids;

const runWordsOf = (card: ActionCard) => {
  const state = cardState(card.row);
  return { title: state.row_title ?? state.title, run: state.run };
};

type BackgroundRunsOptions = {
  // Keeps a card that did not go through listed, so it shows its failure or its fields
  readonly retainCard: (cardId: string) => void;
  readonly closeCard: (cardId: string) => void;
};

// A card the user runs leaves the list at once, and a toast follows the run.
// A run that does not go through brings the card back
export const useBackgroundRuns = ({
  retainCard,
  closeCard,
}: BackgroundRunsOptions) => {
  const [launchedCardIds, setLaunchedCardIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  // A refused card runs in place on its next press, so its field errors show in the card
  const [inPlaceCardIds, setInPlaceCardIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const launchCard = (card: ActionCard) => {
    setLaunchedCardIds((current) => new Set(current).add(card.id));
    const { title, run } = runWordsOf(card);
    toast.custom(
      () => (
        <CardRunToast
          label={run?.running_label ?? RUNNING_TEXT}
          title={title}
          done={false}
        />
      ),
      { id: toastIdOf(card.id), duration: Infinity }
    );
  };

  const landCard = (card: ActionCard, end: RunEnd) => {
    setLaunchedCardIds((current) => withoutId(current, card.id));
    const id = toastIdOf(card.id);
    const endId = endToastIdOf(card.id);
    const { title, run } = runWordsOf(card);
    if (!(end === 'settled' && DONE_STATUSES.has(card.status)))
      toast.dismiss(id);
    if (end === 'threw') {
      // The run command has already said why in its own toast
      retainCard(card.id);
      return;
    }
    if (end === 'refused') {
      setInPlaceCardIds((current) => new Set(current).add(card.id));
      toast.error(REFUSED_TEXT, { id: endId, description: title });
      return;
    }
    if (card.status === 'failed') {
      retainCard(card.id);
      toast.error(run?.failed_text ?? FAILED_TEXT, {
        id: endId,
        description: title,
      });
      return;
    }
    closeCard(card.id);
    if (DONE_STATUSES.has(card.status)) {
      toast.custom(
        () => (
          <CardRunToast
            label={run?.done_text ?? DONE_TEXT}
            title={title}
            done
          />
        ),
        { id, duration: Infinity }
      );
      setTimeout(() => toast.dismiss(id), DONE_TOAST_MS);
    } else if (card.status === 'running') {
      toast.message(STILL_RUNNING_TEXT, { id: endId, description: title });
    }
  };

  return {
    launchedCardIds,
    launchCard,
    landCard,
    runsInBackground: (cardId: string) => !inPlaceCardIds.has(cardId),
  };
};
